import { useTranslation } from 'react-i18next';
import type { AiPreview } from '@/mocks/dashboard';

interface Props {
  previews: AiPreview[];
}

const statusColors: Record<string, string> = {
  queued: 'bg-ink-50 text-ink-400 border-ink-200',
  processing: 'bg-warm-50 text-warm-700 border-warm-200',
  completed: 'bg-ink-50 text-ink-600 border-ink-200',
  failed: 'bg-warm-50 text-warm-800 border-warm-300',
};

export default function DashboardAiPreviews({ previews }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-magic-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.aiPreviews.title')}</h3>
        </div>
        <a href="/my/finish-previews" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {previews.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-magic-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.aiPreviews.empty')}</p>
        </div>
      ) : (
        <div className="grid gap-3 p-4 md:p-5">
          {previews.map((p) => (
            <div key={p.id} className="flex gap-3 md:gap-4">
              <div className="flex gap-1.5 shrink-0">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-cream-100">
                  <img
                    src={p.originalImage}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-cream-100 relative">
                  {p.generatedImage ? (
                    <img
                      src={p.generatedImage}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-cream-400 text-xl" />
                    </div>
                  )}
                  {p.status === 'completed' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-ink-600 text-xs" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ink-600 truncate">{p.productName}</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusColors[p.status]}`}>
                    {t(`dashboard.aiStatus.${p.status}`)}
                  </span>
                </div>
                <p className="text-xs text-ink-300 mt-0.5">{p.finishType}</p>
                <p className="text-[11px] text-ink-200 mt-1">{new Date(p.createdAt).toLocaleDateString()}</p>
                <div className="flex items-center gap-2 mt-2">
                  <a
                    href={`/my/finish-previews/${p.id}`}
                    className="text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap"
                  >
                    {t('dashboard.aiPreviews.view')}
                  </a>
                  {p.status === 'completed' && !p.quoteRequestId && (
                    <>
                      <span className="text-ink-200">|</span>
                      <a
                        href="/packaging"
                        className="text-xs font-medium text-warm-600 hover:text-warm-700 transition-colors whitespace-nowrap"
                      >
                        {t('dashboard.aiPreviews.requestQuote')}
                      </a>
                    </>
                  )}
                </div>
                {p.status === 'completed' && !p.quoteRequestId && (
                  <p className="text-[11px] text-warm-600 mt-1 font-medium">{t('dashboard.aiPreviews.readyCta')}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}